/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { RoutesWrapper } from "routes";

import { Modal, Overlay } from "./components";
import { useUI } from "./state";
import cn from "classnames";
import { useEffect } from "react";
import Intercom from "@intercom/messenger-js-sdk";
import { getLastThemingUpdate } from "lib/organizationApi";
import { getEnvs } from "state/EnvironmentsContext/EnvContext";
import MetaTitle from "components/HeadTags";
import { useDirection } from "hooks/useDirection";
import useEventRouting from "hooks/useEventRouting";
import { useLocation } from "react-router-dom";

const getFullPageUrl = (): string => {
  const href = globalThis.location?.href;
  return typeof href === "string" ? href : "";
};

type IntercomFn = (action: string, data?: object) => void;

const getIntercom = (): IntercomFn | undefined => {
  const fn = (globalThis as unknown as { Intercom?: IntercomFn }).Intercom;
  return typeof fn === "function" ? fn : undefined;
};

const syncIntercomSource = (
  pathname: string,
  eventId: string | number | undefined,
) => {
  const intercom = getIntercom();
  if (!intercom) return;

  intercom("update", {
    custom_attributes: {
      source: getFullPageUrl(),
      source_page: pathname,
      ...(eventId ? { source_event_id: String(eventId) } : {}),
    },
  });
};

const Layout = () => {
  const { modalData, isModalOpen, closeModal, colorScheme } = useUI();
  // Initialize direction management (sets document.dir and document.lang)
  const { isRTL } = useDirection();
  const { pathname, eventId } = useEventRouting();
  const location = useLocation();

  const getLastTimeThemeUpdated = async () => {
    const prevVal = localStorage.getItem("last_theme_Updated") || "";
    const { data } = await getLastThemingUpdate();
    localStorage.setItem("last_theme_Updated", data.latest_time);

    const prevDate = new Date(prevVal);
    const newDate = new Date(data.latest_time);

    if (prevDate < newDate) {
      document.querySelectorAll("img").forEach((img) => {
        fetch(img.src, { cache: "reload" })
          .then((response) => response.blob())
          .then((blob) => {
            const newUrl = URL.createObjectURL(blob);
            img.src = newUrl;
          });
      });
    }
  };

  useEffect(() => {
    getLastTimeThemeUpdated().catch(() => {});
  }, []);

  useEffect(() => {
    const intercomAppId = getEnvs((state) => state.intercom);
    if (intercomAppId) {
      Intercom({
        app_id: intercomAppId,
        alignment: isRTL ? "left" : "right",
      });
      syncIntercomSource(pathname, eventId);
    } else {
      console.error("Intercom app ID is not defined.");
    }
  }, [isRTL, pathname, eventId]);

  useEffect(() => {
    syncIntercomSource(pathname, eventId);
  }, [pathname, location.search, location.hash, eventId]);

  return (
    <div
      className={cn(
        "-bg--theme-colors-surface-neutral-level00 min-w-full relative transition-colors duration-300",
        colorScheme,
      )}
    >
      <MetaTitle />
      <Overlay isOpen={isModalOpen} onClick={closeModal}>
        {isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            content={modalData.content}
            onCancel={closeModal}
            variant={modalData.variant}
            showCloseButton={modalData.showCloseButton}
          />
        )}
      </Overlay>
      <RoutesWrapper />
    </div>
  );
};

export default Layout;
